import { useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router-dom';
import { authApi, ApiError } from '../api';
import { useTheme } from '../themes/ThemeContext';

export function LoginPage() {
  const theme = useTheme();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const tokens = await authApi.login(email, password);
      const redirectUri = searchParams.get('redirect_uri');
      if (redirectUri) {
        // OIDC-style handoff: pass token back to the caller.
        window.location.href = `${redirectUri}?access_token=${tokens.accessToken}`;
        return;
      }
      // Standalone portal login — no redirect target, show success state.
      setSuccess(true);
    } catch (err) {
      if (err instanceof ApiError) {
        setError('Invalid email or password');
      } else {
        setError('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div style={{ background: theme.colors.background, color: theme.colors.text, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: '100%', maxWidth: 400, padding: 32, textAlign: 'center' }}>
          <img src={theme.logoUrl} alt={theme.displayName} style={{ height: 48, marginBottom: 16 }} />
          <h1 style={{ fontSize: 24, fontWeight: 600, marginBottom: 16 }}>Signed in</h1>
          <p style={{ fontSize: 14, color: theme.colors.textMuted, marginBottom: 24 }}>
            You're logged in as <strong>{email}</strong>.
          </p>
          <p style={{ fontSize: 14, color: theme.colors.textMuted }}>
            To use an application, open it and it will redirect you here to complete sign-in automatically.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ background: theme.colors.background, color: theme.colors.text, minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: '100%', maxWidth: 400, padding: 32 }}>
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <img src={theme.logoUrl} alt={theme.displayName} style={{ height: 48, marginBottom: 16 }} />
          <h1 style={{ fontSize: 24, fontWeight: 600 }}>{theme.copy.loginTitle}</h1>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 16 }}>
            <label htmlFor="email" style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>Email</label>
            <input
              id="email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #d1d5db', fontSize: 16, boxSizing: 'border-box' }}
            />
          </div>

          <div style={{ marginBottom: 16 }}>
            <label htmlFor="password" style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>Password</label>
            <input
              id="password"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              style={{ width: '100%', padding: 10, borderRadius: 6, border: '1px solid #d1d5db', fontSize: 16, boxSizing: 'border-box' }}
            />
          </div>

          {error && (
            <div style={{ color: '#dc2626', fontSize: 14, marginBottom: 16 }}>{error}</div>
          )}

          <button
            type="submit"
            disabled={loading}
            style={{ width: '100%', padding: 12, borderRadius: 6, border: 'none', background: theme.colors.primary, color: '#fff', fontSize: 16, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}
          >
            {loading ? 'Logging in...' : 'Log In'}
          </button>
        </form>

        <div style={{ textAlign: 'center', marginTop: 16, fontSize: 14 }}>
          <a href={`/signup${window.location.search}`} style={{ color: theme.colors.primary }}>Create an account</a>
          {' | '}
          <a href={`/reset-password${window.location.search}`} style={{ color: theme.colors.primary }}>Forgot password?</a>
        </div>
      </div>
    </div>
  );
}
